/**
 * My Dashboard — the agent landing page (/agent).
 *
 * "How is my queue doing" in one view: a live status card (current status,
 * elapsed, today's ready/available/break totals + the status switcher), the
 * conversation mix by status (the tiles double as the page-wide status
 * filter), reply-speed and deadline figures, then channels / categories /
 * subcategories, and the session log at the bottom.
 *
 * All conversation figures come from GET /agent/dashboard/summary via
 * useAgentDashboard, which re-fetches on `conversation:list:bump`. Session
 * state is the local agentStore (socket-fed); no extra requests.
 */
import { useState, useEffect, useMemo, type JSX } from 'react'
import {
  ArrowRight,
  BarChart3,
  LayoutGrid,
  MessagesSquare,
  Mic,
  PieChart,
  RefreshCw,
  Tag,
  Timer,
  TrendingUp,
} from 'lucide-react'
import { Link } from 'react-router'
import { Chart } from 'primereact/chart'
import {
  CHANNEL_META,
  sourceMeta,
  type ChannelMeta,
} from '../../components/conversations/channelMeta.js'
import { useAuthStore } from '../../store/authStore.js'
import { useAgentStore } from '../../store/agentStore.js'
import type { SessionHistory } from '../../store/agentStore.js'
import { useAgentDashboard, type AgentDashboardSummary } from '../../hooks/useAgentDashboard.js'
import SessionHistoryTable from '../../components/agent/SessionHistoryTable.js'
import Select from '../../components/ui/Select.js'
import { timestampMs } from '../../utils/timezone.js'
import DatePicker from '../../components/ui/DatePicker.js'
import { AnalyticsCanvas, Meter, SectionLabel } from '../../components/reports/analytics/AnalyticsKit.js'
import { VIZ, formatCount, formatPct, formatSeconds } from '../../components/reports/analytics/viz.js'

const CARD = 'rounded-[3px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]'

// ---------------------------------------------------------------------------
// Static dimensions
// ---------------------------------------------------------------------------

interface StatusDim {
  key: 'open' | 'closed' | 'on_hold' | 'reopen'
  label: string
  color: string
  /** Short state tag shown as a pill on the tile (Active / Pending / …). */
  tag: string
  badge: string
  hover: string
}

const STATUS_DIMS: StatusDim[] = [
  {
    key: 'open',
    label: 'Open',
    color: '#2563EB',
    tag: 'Active',
    badge: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    hover: 'hover:border-blue-300',
  },
  {
    key: 'on_hold',
    label: 'On hold',
    color: '#F59E0B',
    tag: 'Pending',
    badge: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    hover: 'hover:border-amber-300',
  },
  {
    key: 'closed',
    label: 'Closed',
    color: '#10B981',
    tag: 'Resolved',
    badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    hover: 'hover:border-emerald-300',
  },
  {
    key: 'reopen',
    label: 'Reopened',
    color: '#E11D48',
    tag: 'Review',
    badge: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    hover: 'hover:border-rose-300',
  },
]

/** Categorical palette for category/subcategory rows and the subcategory donut. */
const TOPIC_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#0EA5E9', '#8B5CF6', '#F43F5E', '#64748B']

/**
 * Channel rows come from the same CHANNEL_META/sourceMeta registry the inbox
 * uses (real brand glyphs), so a channel looks identical here and in the
 * conversation list. The API's `source` key is already
 * COALESCE(metadata.source, channel_origin), so each raw key resolves through
 * channel meta first, then the source-icon overrides.
 */

/** Aliases that fold near-duplicate keys onto one row — `twitter` predates
 *  the `x` channel value, and a phone call arrives as either `call` (source)
 *  or `voice` (channel). `chat` deliberately stays its own row ("Live Chat"):
 *  the inbox labels it "Web", but folding the two together hid the split. */
const CHANNEL_ALIAS: Record<string, string> = {
  twitter: 'x',
  call: 'voice',
  'walk-in': 'walkin',
}

/** Label overrides — source keys with no channel meta (whose fallback label
 *  would be the raw machine key), plus `chat`, which channelMeta calls "Web"
 *  but which deserves its own name here so it isn't indistinguishable from
 *  the `web` channel. */
const SOURCE_LABELS: Record<string, string> = {
  chat: 'Live Chat',
  agent_portal: 'Agent Portal',
  client_portal: 'Client Portal',
  walkin: 'Walk-in',
  postal: 'Postal Mail',
  postalmail: 'Postal Mail',
  unknown: 'Unknown',
}

const prettify = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

function channelMetaFor(rawKey: string): ChannelMeta {
  const key = rawKey.toLowerCase()
  const label = SOURCE_LABELS[key]
  const known = CHANNEL_META[key]
  if (known) return label ? { ...known, label } : known
  const resolved = sourceMeta(key, undefined)
  return { ...resolved, label: label ?? prettify(key) }
}

/** Doughnut segment colors — the channel's own brand hue, matching the icon
 *  chips. Unknown sources fall back to the neutral series slot. */
const CHANNEL_COLORS: Record<string, string> = {
  voice: '#EA580C',
  chat: '#7C3AED',
  web: '#4F46E5',
  email: '#C026D3',
  sms: '#16A34A',
  whatsapp: '#059669',
  telegram: '#0EA5E9',
  facebook: '#1D4ED8',
  instagram: '#DB2777',
  youtube: '#DC2626',
  x: '#0284C7',
  unknown: '#8A94A6',
}

const channelColor = (key: string, index: number): string =>
  CHANNEL_COLORS[key] ?? VIZ.series[index % VIZ.series.length] ?? '#8A94A6'

// ---------------------------------------------------------------------------
// Small utilities
// ---------------------------------------------------------------------------

function hhmmss(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
}

/** Hours (float, from the API) → "2h 24m". Easier to read than "2.4 Hr(s)". */
const fmtHours = (hours: number): string =>
  hours > 0 ? formatSeconds(Math.round(hours * 3600)) : '—'

/**
 * Session totals — the same live-accumulation rule SessionHistoryTable uses:
 * the current (last) entry's matching bucket keeps ticking until the next
 * status change.
 */
function sessionTotals(history: readonly SessionHistory[]): {
  ready: number
  available: number
  breakTime: number
} {
  const now = Date.now()
  let ready = 0
  let available = 0
  let breakTime = 0
  history.forEach((e, i) => {
    let r = e.readyTime
    let a = e.availableTime
    let b = e.breakTime
    if (i === history.length - 1) {
      const elapsed = Math.max(0, Math.floor((now - timestampMs(e.time)) / 1000))
      if (e.key === 'login') r += elapsed
      else if (e.key === 'available') a += elapsed
      else if (e.key !== 'logout') b += elapsed
    }
    ready += r
    available += a
    breakTime += b
  })
  return { ready, available, breakTime }
}

/** Shared doughnut options — tooltip shows count + share of the given total. */
function donutOptions(total: number) {
  return {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '70%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: VIZ.ink,
        titleColor: '#FFFFFF',
        bodyColor: '#DCE3EF',
        borderWidth: 0,
        padding: 10,
        cornerRadius: 4,
        callbacks: {
          label: (ctx: { label?: string; parsed: number }) =>
            ` ${ctx.label}: ${formatCount(ctx.parsed)} (${
              total > 0 ? formatPct((ctx.parsed / total) * 100, 1) : '0.0%'
            })`,
        },
      },
    },
  }
}

// ---------------------------------------------------------------------------
// Status card (hero)
// ---------------------------------------------------------------------------

/** One tinted counter chip in the status card's stat row. */
function HeroChip({
  label,
  value,
  Icon,
  tone,
}: {
  label: string
  value: string
  Icon?: typeof Timer
  tone: 'indigo' | 'slate' | 'emerald'
}): JSX.Element {
  const tones = {
    indigo: { wrap: 'border-indigo-100 bg-indigo-50/40', label: 'text-indigo-700', value: 'text-slate-900' },
    slate: { wrap: 'border-slate-200/80 bg-slate-50/70', label: 'text-slate-500', value: 'text-slate-700' },
    emerald: { wrap: 'border-emerald-200/70 bg-emerald-50/50', label: 'text-emerald-700', value: 'text-emerald-800' },
  }[tone]
  return (
    <div className={`rounded-[3px] border px-3.5 py-2 ${tones.wrap}`}>
      <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${tones.label}`}>
        {Icon !== undefined && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={`mt-0.5 font-mono text-sm font-bold tabular-nums tracking-tight ${tones.value}`}>
        {value}
      </div>
    </div>
  )
}

function StatusCard(): JSX.Element {
  const userStatus = useAgentStore((s) => s.userStatus)
  const userState = useAgentStore((s) => s.userState)
  const sessionHistory = useAgentStore((s) => s.sessionHistory)

  const lastEntry = sessionHistory[sessionHistory.length - 1]
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  const elapsed = lastEntry
    ? Math.max(0, Math.floor((now - timestampMs(lastEntry.time)) / 1000))
    : 0
  const totals = useMemo(() => sessionTotals(sessionHistory), [sessionHistory, now])
  const isLive = userStatus.key !== 'logout'

  return (
    <section className={`${CARD} p-5`}>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        {/* Status + identity */}
        <div className="flex items-center gap-4">
          <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-[3px] border border-indigo-100/80 bg-indigo-50 text-indigo-600">
            <Mic className="h-6 w-6" />
            {isLive && (
              <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
              </span>
            )}
          </span>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                {isLive && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                )}
                <span
                  className={`relative inline-flex h-2 w-2 rounded-full ${isLive ? 'bg-emerald-500' : 'bg-slate-300'}`}
                />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Current status
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="text-2xl font-bold leading-none tracking-tight text-[#0f172a]">
                {userStatus.text}
              </h2>
              {userStatus.key === 'available' && (
                <span className="inline-flex items-center rounded-[3px] bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-700 ring-1 ring-inset ring-indigo-500/20">
                  {userState}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Live counters */}
        <div className="flex flex-wrap items-center gap-3">
          <HeroChip label="In status" value={hhmmss(elapsed)} Icon={Timer} tone="indigo" />
          <HeroChip label="Ready today" value={formatSeconds(totals.ready)} tone="slate" />
          <HeroChip label="Available today" value={formatSeconds(totals.available)} tone="emerald" />
          <HeroChip label="On break today" value={formatSeconds(totals.breakTime)} tone="slate" />
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

/** A status tile that is also the page filter — click to include/exclude. */
function StatusTile({
  dim,
  count,
  share,
  selected,
  onToggle,
}: {
  dim: StatusDim
  count: number
  share: number | null
  selected: boolean
  onToggle: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      title={
        selected
          ? `Exclude ${dim.label} from the dashboard`
          : `Include ${dim.label} in the dashboard`
      }
      className={`${CARD} p-4 text-left transition-all duration-200 ${
        selected ? `${dim.hover} hover:shadow-[0_6px_16px_-4px_rgba(15,23,42,0.10)]` : 'opacity-50 hover:opacity-75'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: dim.color }} aria-hidden="true" />
          <span className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-600">
            {dim.label}
          </span>
        </span>
        <span
          className={`inline-flex shrink-0 items-center rounded-[3px] px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${dim.badge}`}
        >
          {dim.tag}
        </span>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="font-mono text-3xl font-extrabold tabular-nums tracking-tight text-[#0f172a]">
          {formatCount(count)}
        </span>
        <span className="font-mono text-[11px] font-medium tabular-nums text-slate-500">
          {share === null ? 'of all' : `${formatPct(share, 1)} of all`}
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(share ?? 0, 100)}%`, background: dim.color }}
        />
      </div>
    </button>
  )
}

/** One cell in the reply-speed grid — grey well with an optional state pill. */
function SlaCell({
  label,
  value,
  hint,
  pill,
}: {
  label: string
  value: string
  hint: string
  pill?: { text: string; cls: string } | undefined
}): JSX.Element {
  return (
    <div className="rounded-[3px] border border-slate-200/60 bg-slate-50/70 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
        {pill !== undefined && (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${pill.cls}`}
          >
            {pill.text}
          </span>
        )}
      </div>
      <div
        className={`mt-2 font-mono text-2xl font-bold tabular-nums ${value === '—' ? 'text-slate-400' : 'text-[#0f172a]'}`}
      >
        {value}
      </div>
      <p className="mt-1 text-[11.5px] text-slate-500">{hint}</p>
    </div>
  )
}

/** Doughnut with the total in the hole — used by channels and subcategories. */
function DonutBlock({
  data,
  options,
  total,
  size,
}: {
  data: { labels: string[]; datasets: { data: number[]; backgroundColor: string[]; borderColor: string; borderWidth: number; hoverOffset: number }[] }
  options: ReturnType<typeof donutOptions>
  total: number
  size: number
}): JSX.Element {
  return (
    <div className="relative shrink-0" style={{ height: size, width: size }}>
      <Chart type="doughnut" data={data} options={options} className="h-full w-full" />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-mono text-3xl font-extrabold tabular-nums leading-none tracking-tight text-[#0f172a]">
          {formatCount(total)}
        </span>
        <span className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          total
        </span>
      </div>
    </div>
  )
}

/** Card header — icon chip + title + subtitle + optional count pill. */
function CardHead({
  Icon,
  iconCls,
  title,
  subtitle,
  pill,
}: {
  Icon: typeof BarChart3
  iconCls: string
  title: string
  subtitle: string
  pill?: string | undefined
}): JSX.Element {
  return (
    <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] ${iconCls}`}>
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h3 className="truncate text-sm font-semibold text-[#0f172a]">{title}</h3>
            {pill !== undefined && (
              <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">
                {pill}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-[11.5px] text-slate-500">{subtitle}</p>
        </div>
      </div>
    </header>
  )
}

/** Derived alert row — `to` deep-links into the inbox so the row is
 *  actionable, not just a warning. */
interface AttentionRow {
  id: string
  title: string
  detail: string
  value: string
  tone: 'warning' | 'critical'
  to: string
}

const ATTN_DOT = { warning: 'bg-[#FAB219]', critical: 'bg-[#D03B3B]' } as const
const ATTN_INK = { warning: 'text-[#A9740A]', critical: 'text-[#B33131]' } as const

function DashboardSkeleton(): JSX.Element {
  return (
    <div role="status" aria-label="Loading dashboard" className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={`h-[112px] animate-pulse ${CARD}`} />
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className={`h-[280px] animate-pulse ${CARD}`} />
        ))}
      </div>
      <div className={`h-[340px] animate-pulse ${CARD}`} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Date range
// ---------------------------------------------------------------------------

/** Filter state survives navigation — the dashboard is a landing page, so
 *  losing the chosen range/statuses on every visit is friction. */
function useStickyState<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw === null ? initial : (JSON.parse(raw) as T)
    } catch {
      return initial
    }
  })
  const set = (v: T | ((prev: T) => T)): void => {
    setValue((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // storage unavailable (private mode) — the session state still works
      }
      return next
    })
  }
  return [value, set]
}

const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'month', label: 'This month' },
  { value: 'custom', label: 'Custom range' },
]

const toDateInput = (d: Date): string => d.toISOString().slice(0, 10)

function presetRange(key: string): { from: string; to: string } {
  const today = new Date()
  const day = 24 * 60 * 60 * 1000
  switch (key) {
    case 'today':
      return { from: toDateInput(today), to: toDateInput(today) }
    case 'yesterday':
      return { from: toDateInput(new Date(today.getTime() - day)), to: toDateInput(new Date(today.getTime() - day)) }
    case '7d':
      return { from: toDateInput(new Date(today.getTime() - 6 * day)), to: toDateInput(today) }
    case '30d':
      return { from: toDateInput(new Date(today.getTime() - 29 * day)), to: toDateInput(today) }
    case 'month':
      return { from: toDateInput(new Date(today.getFullYear(), today.getMonth(), 1)), to: toDateInput(today) }
    default:
      return { from: '', to: '' }
  }
}

// ---------------------------------------------------------------------------
// Demo data — ?demo=1 fills the dashboard for design review only. Real API
// data is untouched; drop the param to go back to live numbers.
// ---------------------------------------------------------------------------

const DEMO_SUMMARY: AgentDashboardSummary = {
  statusCounts: { open: 16, closed: 28, on_hold: 4, reopen: 2 },
  total: 50,
  sla: { avgResponseHours: 0.3, avgResolutionHours: 4.6 },
  escalated: { response: 3, resolution: 1 },
  sourceCounts: {
    chat: 24,
    web: 9,
    voice: 8,
    whatsapp: 4,
    email: 3,
    facebook: 1,
    walkin: 1,
  },
  categoryCounts: [
    { label: 'Support - General', count: 18 },
    { label: 'Billing - General', count: 12 },
    { label: 'Sales - General', count: 8 },
    { label: 'Technical', count: 6 },
    { label: 'Complaint', count: 4 },
    { label: 'Other', count: 2 },
  ],
  subcategoryCounts: [
    { label: 'General Inquiry', count: 14 },
    { label: 'Invoice Issue', count: 9 },
    { label: 'Lead', count: 8 },
    { label: 'Password Reset', count: 7 },
    { label: 'Refund Request', count: 5 },
    { label: 'Escalated billing', count: 4 },
    { label: 'Service Outage', count: 3 },
  ],
  range: { from: null, to: null },
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function MyDashboardPage(): JSX.Element {
  const [fromDate, setFromDate] = useStickyState('kc3.myDashboard.from', '')
  const [toDate, setToDate] = useStickyState('kc3.myDashboard.to', '')
  const [range, setRange] = useStickyState('kc3.myDashboard.range', 'all')
  const applyRange = (key: string): void => {
    setRange(key)
    if (key === 'custom') return
    const { from, to } = presetRange(key)
    setFromDate(from)
    setToDate(to)
  }
  // Status tiles double as the filter: deselecting one removes that status
  // from every widget on the page (totals, SLA, source counts). A stored list
  // can contain keys the schema no longer knows — intersect on read.
  const [rawStatuses, setSelectedStatuses] = useStickyState<string[]>(
    'kc3.myDashboard.statuses',
    STATUS_DIMS.map((s) => s.key),
  )
  const selectedStatuses = rawStatuses.filter((k) => STATUS_DIMS.some((d) => d.key === k))
  const toggleStatus = (k: string): void => {
    setSelectedStatuses((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]))
  }
  const user = useAuthStore((s) => s.user)
  const live = useAgentDashboard(fromDate, toDate, selectedStatuses)
  // Design-review mode — seed via ?demo=1 or toggle from the header pill.
  // Demo mode is review scaffolding — dev builds only, so a production
  // bundle can never present fabricated metrics as real.
  const demoAllowed = import.meta.env.DEV
  const [demo, setDemo] = useState(() => demoAllowed && new URLSearchParams(window.location.search).has('demo'))
  const data = demo ? DEMO_SUMMARY : live.data
  const loading = demo ? false : live.loading
  const refetch = live.refetch

  const agentName = user?.fullName?.trim() || user?.email?.split('@')[0] || 'Agent'
  const initialLoading = loading && data.total === 0
  const greeting =
    new Date().getHours() < 12
      ? 'Good morning'
      : new Date().getHours() < 17
        ? 'Good afternoon'
        : 'Good evening'

  const shareOfTotal = (n: number): number | null => (data.total > 0 ? (n / data.total) * 100 : null)

  const channels = useMemo(() => {
    // Fold aliases onto canonical keys first, then resolve each key's identity
    // through channelMeta. Every supported channel gets a row — zero share is
    // still information — and any source key the API returns that isn't a
    // channel (walkin, client_portal, …) is appended after them.
    const byKey = new Map<string, number>()
    for (const [raw, v] of Object.entries(data.sourceCounts)) {
      const key = CHANNEL_ALIAS[raw.toLowerCase()] ?? raw.toLowerCase()
      byKey.set(key, (byKey.get(key) ?? 0) + v)
    }
    const rows = new Map<string, number>(Object.keys(CHANNEL_META).map((k) => [k, byKey.get(k) ?? 0]))
    for (const [key, v] of byKey) {
      if (!rows.has(key)) rows.set(key, v)
    }
    return [...rows.entries()]
      .map(([key, value]) => ({ key, value, meta: channelMetaFor(key) }))
      .sort((a, b) => b.value - a.value || a.meta.label.localeCompare(b.meta.label))
  }, [data])
  const chartChannels = useMemo(() => channels.filter((c) => c.value > 0), [channels])
  const topChannel = chartChannels[0]

  const channelChart = useMemo(
    () => ({
      labels: chartChannels.map((c) => c.meta.label),
      datasets: [
        {
          data: chartChannels.map((c) => c.value),
          backgroundColor: chartChannels.map((c, i) => channelColor(c.key, i)),
          borderColor: VIZ.surface,
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }),
    [chartChannels],
  )

  // Derived "worth a look" rows — no extra fetch, just thresholds on the
  // summary the page already has. Ordered worst-first; absent when all clear.
  const attention = useMemo<AttentionRow[]>(() => {
    const items: AttentionRow[] = []
    if (data.statusCounts.reopen > 0) {
      items.push({
        id: 'reopen',
        title: `${formatCount(data.statusCounts.reopen)} conversation${data.statusCounts.reopen === 1 ? '' : 's'} reopened`,
        detail: 'Closed but came back — check the fix held',
        value: formatPct(shareOfTotal(data.statusCounts.reopen), 1),
        tone: 'critical',
        to: '/agent/conversation/inbox',
      })
    }
    if (data.escalated.resolution > 0) {
      items.push({
        id: 'esc-res',
        title: `${formatCount(data.escalated.resolution)} missed the resolution deadline`,
        detail: 'Still open (or closed late) past their SLA — open them first',
        value: formatPct(shareOfTotal(data.escalated.resolution), 1),
        tone: 'critical',
        to: '/agent/conversation/inbox?filter=beyond-sla',
      })
    }
    if (data.escalated.response > 0) {
      items.push({
        id: 'esc-resp',
        title: `${formatCount(data.escalated.response)} got a late first reply`,
        detail: 'First-response SLA was missed in this range',
        value: formatPct(shareOfTotal(data.escalated.response), 1),
        tone: 'warning',
        to: '/agent/conversation/inbox?filter=beyond-sla',
      })
    }
    const onHoldShare = shareOfTotal(data.statusCounts.on_hold) ?? 0
    if (data.statusCounts.on_hold >= 3 && onHoldShare > 25) {
      items.push({
        id: 'on-hold',
        title: `${formatCount(data.statusCounts.on_hold)} conversations waiting on hold`,
        detail: 'Over a quarter of your queue is parked — pick them up before they go stale',
        value: formatPct(onHoldShare, 1),
        tone: 'warning',
        to: '/agent/conversation/inbox',
      })
    }
    return items
  }, [data])

  const categories = useMemo(() => data.categoryCounts.slice(0, 8), [data])
  const subcategories = useMemo(() => data.subcategoryCounts.slice(0, 8), [data])
  const subcatChart = useMemo(
    () => ({
      labels: subcategories.map((s) => s.label),
      datasets: [
        {
          data: subcategories.map((s) => s.count),
          backgroundColor: subcategories.map((_, i) => TOPIC_COLORS[i % TOPIC_COLORS.length] ?? '#8A94A6'),
          borderColor: VIZ.surface,
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }),
    [subcategories],
  )

  const channelOpts = useMemo(() => donutOptions(data.total), [data.total])
  const subcatOpts = useMemo(() => donutOptions(data.total), [data.total])

  // One sentence that says what the page says — the numbers below fill in why.
  const summaryLine = useMemo(() => {
    if (initialLoading || data.total === 0) return null
    const parts: string[] = []
    if (data.statusCounts.open > 0) parts.push(`${formatCount(data.statusCounts.open)} open`)
    if (data.statusCounts.on_hold > 0) parts.push(`${formatCount(data.statusCounts.on_hold)} on hold`)
    if (data.statusCounts.reopen > 0) parts.push(`${formatCount(data.statusCounts.reopen)} reopened`)
    const first = `${formatCount(data.total)} conversation${data.total === 1 ? '' : 's'}`
    const breakdown = parts.length > 0 ? ` — ${parts.join(', ')}` : ''
    const speed =
      data.sla.avgResponseHours > 0 ? ` · avg first reply ${fmtHours(data.sla.avgResponseHours)}` : ''
    return `${first}${breakdown}${speed}`
  }, [data, initialLoading])

  const meterSegments = STATUS_DIMS.filter((d) => selectedStatuses.includes(d.key)).map((d) => ({
    label: d.label,
    value: data.statusCounts[d.key],
    color: d.color,
  }))

  return (
    <AnalyticsCanvas>
      {/* Header — title, live pill, greeting + summary, range controls */}
      <section className={`${CARD} px-5 py-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight text-[#0f172a]">My Dashboard</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                Live Synced
              </span>
            </div>
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-slate-500">
              <span>
                {greeting}, <strong className="font-semibold text-slate-700">{agentName}</strong>
              </span>
              <span className="text-slate-300">•</span>
              <span>{fromDate && toDate ? `${fromDate} → ${toDate}` : 'All time'}</span>
              {summaryLine !== null && (
                <>
                  <span className="text-slate-300">|</span>
                  <span className="font-medium text-slate-600">{summaryLine}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            {demoAllowed && (
              <button
                type="button"
                onClick={() => setDemo((d) => !d)}
                title="Show sample data for design review"
                className={`inline-flex h-9 items-center rounded-[3px] border px-3 text-[11px] font-semibold transition ${
                  demo
                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                    : 'border-slate-300 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                {demo ? 'Demo data on' : 'Demo data'}
              </button>
            )}
            <Select
              ariaLabel="Quick range"
              value={range}
              onChange={applyRange}
              options={RANGE_OPTIONS}
              className="w-[150px]"
            />
            <DatePicker
              ariaLabel="From date"
              label="From"
              value={fromDate}
              onChange={(v) => {
                setFromDate(v)
                setRange('custom')
              }}
            />
            <DatePicker
              ariaLabel="To date"
              label="To"
              value={toDate}
              onChange={(v) => {
                setToDate(v)
                setRange('custom')
              }}
            />
            <button
              type="button"
              onClick={() => void refetch()}
              aria-label="Refresh metrics"
              title="Refresh metrics"
              className="inline-flex h-9 w-9 items-center justify-center rounded-[3px] border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </section>

      <StatusCard />

      {initialLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          {/* Status tiles = the page filter */}
          <section className="flex flex-col gap-3">
            <SectionLabel note="Click a tile to show or hide that status everywhere below">
              Your conversations by status
            </SectionLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {STATUS_DIMS.map((dim) => (
                <StatusTile
                  key={dim.key}
                  dim={dim}
                  count={data.statusCounts[dim.key]}
                  share={shareOfTotal(data.statusCounts[dim.key])}
                  selected={selectedStatuses.includes(dim.key)}
                  onToggle={() => toggleStatus(dim.key)}
                />
              ))}
            </div>
            {selectedStatuses.length < STATUS_DIMS.length && (
              <p className="text-[11.5px] font-medium text-slate-400">
                Showing {selectedStatuses.length === 0 ? 'no' : selectedStatuses.length} of{' '}
                {STATUS_DIMS.length} statuses — click a tile to toggle it back on.
              </p>
            )}
          </section>

          {/* Worth a look — derived from the same summary, worst first. Each
              row deep-links into the inbox view where the work actually is. */}
          {attention.length > 0 && (
            <section className={`${CARD} px-4`}>
              <ul className="divide-y divide-slate-100">
                {attention.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.to}
                      className="group flex items-center gap-3 py-2.5"
                      title="Open these in your inbox"
                    >
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${ATTN_DOT[item.tone]}`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12.5px] font-medium text-[#0f172a] group-hover:text-indigo-600">
                          {item.title}
                        </p>
                        <p className="truncate text-[11px] text-slate-400">{item.detail}</p>
                      </div>
                      <span
                        className={`shrink-0 font-mono text-[13px] font-semibold tabular-nums ${ATTN_INK[item.tone]}`}
                      >
                        {item.value}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300 transition-colors group-hover:text-indigo-500" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Mix + speed */}
          <section className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className={CARD}>
              <CardHead
                Icon={BarChart3}
                iconCls="bg-indigo-50 text-indigo-600"
                title="Status mix"
                subtitle={`How your ${formatCount(data.total)} conversations split by status`}
              />
              <div className="p-5">
                <Meter
                  segments={meterSegments}
                  emptyMessage="No conversations in this window."
                />
              </div>
            </div>

            <div className={CARD}>
              <CardHead
                Icon={TrendingUp}
                iconCls="bg-blue-50 text-blue-600"
                title="How fast you're replying"
                subtitle="Average times, and how many went past the SLA deadline"
              />
              {data.total === 0 ? (
                <p className="p-5 text-center text-[12px] text-slate-400">
                  No conversations in this window.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
                  <SlaCell
                    label="Avg first reply"
                    value={fmtHours(data.sla.avgResponseHours)}
                    hint="time to your first response"
                  />
                  <SlaCell
                    label="Avg time to resolve"
                    value={fmtHours(data.sla.avgResolutionHours)}
                    hint="from open to closed"
                  />
                  <SlaCell
                    label="Replied late"
                    value={formatCount(data.escalated.response)}
                    hint="first reply past the deadline"
                    pill={
                      data.escalated.response > 0
                        ? { text: 'Late', cls: 'bg-amber-50 text-amber-700 ring-amber-600/20' }
                        : { text: 'On target', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' }
                    }
                  />
                  <SlaCell
                    label="Resolved late"
                    value={formatCount(data.escalated.resolution)}
                    hint="still open or closed past the deadline"
                    pill={
                      data.escalated.resolution > 0
                        ? { text: 'Late', cls: 'bg-rose-50 text-rose-700 ring-rose-600/20' }
                        : { text: 'On target', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' }
                    }
                  />
                </div>
              )}
            </div>
          </section>

          {/* Channels */}
          <section className={CARD}>
            <CardHead
              Icon={MessagesSquare}
              iconCls="bg-violet-50 text-violet-600"
              title="By channel"
              subtitle="Every way a conversation can reach you"
              pill={`${formatCount(data.total)} conversations`}
            />
            {chartChannels.length === 0 ? (
              <p className="p-5 text-center text-[12px] text-slate-400">
                No conversations in this window.
              </p>
            ) : (
              <div className="grid grid-cols-1 items-center gap-8 p-5 lg:grid-cols-12">
                <div className="flex flex-col items-center justify-center lg:col-span-4">
                  <DonutBlock data={channelChart} options={channelOpts} total={data.total} size={208} />
                  {topChannel !== undefined && (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-slate-50 px-3.5 py-1.5 text-[11px] text-slate-600">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: channelColor(topChannel.key, 0) }}
                        aria-hidden="true"
                      />
                      <span>
                        Primary:{' '}
                        <strong className="font-semibold text-slate-800">
                          {topChannel.meta.label} ({formatPct(shareOfTotal(topChannel.value), 1)})
                        </strong>
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 lg:col-span-8">
                  {channels.map((ch) => (
                    <div
                      key={ch.key}
                      className={`flex items-center justify-between rounded-[3px] p-2.5 transition ${
                        ch.value > 0
                          ? 'border border-slate-100 bg-slate-50/60 hover:bg-slate-50'
                          : 'border border-transparent opacity-70 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] ${ch.meta.bg} ${ch.meta.fg}`}
                        >
                          <ch.meta.icon className="h-4 w-4" />
                        </span>
                        <span
                          className={`truncate text-xs ${ch.value > 0 ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'}`}
                          title={ch.meta.label}
                        >
                          {ch.meta.label}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-3 font-mono text-xs tabular-nums">
                        <span className={ch.value > 0 ? 'text-slate-500' : 'text-slate-400'}>
                          {formatPct(shareOfTotal(ch.value), 1)}
                        </span>
                        <span
                          className={`w-8 text-right text-sm ${
                            ch.value > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-400'
                          }`}
                        >
                          {formatCount(ch.value)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Topics */}
          <section className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className={CARD}>
              <CardHead
                Icon={LayoutGrid}
                iconCls="bg-indigo-50 text-indigo-600"
                title="By category"
                subtitle="What your conversations are about"
                pill={`${formatCount(data.categoryCounts.length)} categories`}
              />
              {categories.length === 0 ? (
                <p className="p-5 text-center text-[12px] text-slate-400">
                  No categories in this window.
                </p>
              ) : (
                <div className="space-y-3 p-5">
                  {categories.map((c, i) => {
                    const color = TOPIC_COLORS[i % TOPIC_COLORS.length] ?? '#64748B'
                    const share = shareOfTotal(c.count) ?? 0
                    return (
                      <div
                        key={c.label}
                        className="rounded-[3px] border border-slate-100 bg-slate-50/50 p-3.5 transition hover:border-indigo-200 hover:bg-indigo-50/20"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px]"
                              style={{ backgroundColor: `${color}1A`, color }}
                            >
                              <Tag className="h-3.5 w-3.5" />
                            </span>
                            <span className="truncate text-xs font-semibold text-slate-800">
                              {c.label}
                            </span>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="font-mono text-xs font-bold tabular-nums text-slate-900">
                              {formatCount(c.count)}{' '}
                              <span className="font-sans font-normal text-slate-400">
                                {c.count === 1 ? 'item' : 'items'}
                              </span>
                            </div>
                            <div className="font-mono text-[11px] font-semibold tabular-nums" style={{ color }}>
                              {formatPct(share, 1)}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-200/70">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${share === 0 ? 0 : Math.max(share, 1.5)}%`, background: color }}
                          />
                        </div>
                      </div>
                    )
                  })}
                  <div className="flex items-center justify-between border-t border-slate-100 px-1 pt-2 font-mono text-[10px] text-slate-400">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}
            </div>

            <div className={CARD}>
              <CardHead
                Icon={PieChart}
                iconCls="bg-violet-50 text-violet-600"
                title="By subcategory"
                subtitle="A finer breakdown of the topics"
                pill="Finer breakdown"
              />
              {subcategories.length === 0 ? (
                <p className="p-5 text-center text-[12px] text-slate-400">
                  No subcategories in this window.
                </p>
              ) : (
                <div className="grid grid-cols-1 items-center gap-5 p-5 sm:grid-cols-12">
                  <div className="flex flex-col items-center justify-center sm:col-span-5">
                    <DonutBlock data={subcatChart} options={subcatOpts} total={data.total} size={128} />
                  </div>
                  <div className="space-y-2.5 sm:col-span-7">
                    {subcategories.map((s, i) => {
                      const color = TOPIC_COLORS[i % TOPIC_COLORS.length] ?? '#64748B'
                      return (
                        <div
                          key={s.label}
                          className="flex items-center justify-between rounded-[3px] border p-3 transition"
                          style={{ borderColor: `${color}33`, backgroundColor: `${color}0D` }}
                        >
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ background: color }}
                              aria-hidden="true"
                            />
                            <span className="truncate text-xs font-semibold text-slate-800">
                              {s.label}
                            </span>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="font-mono text-sm font-bold tabular-nums text-slate-900">
                              {formatCount(s.count)}
                            </div>
                            <div className="font-mono text-[11px] font-semibold tabular-nums" style={{ color }}>
                              {formatPct(shareOfTotal(s.count), 1)}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Session log — shared with BreakTimerDialog; it renders its own
          header, so the wrapper only supplies the card frame. */}
      <section className={`overflow-hidden ${CARD}`}>
        <SessionHistoryTable />
      </section>

      <p className="pb-1 text-center text-[11px] text-slate-400">
        Updates live as conversations change
      </p>
    </AnalyticsCanvas>
  )
}
